/**
 * PAA Baseline Fetcher — mapa-time PAA per node z DataForSEO.
 *
 * Audyt mapy (G3) wymaga PAA na poziomie mapy (thematic_nodes.paa_preview)
 * żeby L3 mógł odróżnić stranded spoke od node który ma realny demand signal.
 * Content Engine ma osobny pełny Fan-Out w thematic_node_content.fan_out —
 * NIE TYKAMY, to per-artykuł.
 *
 * Koszt: ~$0.005/node × 200 nodów = ~$1/mapa.
 * Idempotent: skip jeśli paa_fetched_at < 30 dni temu.
 * Concurrency: Promise.all chunks 50 (DataForSEO rate-limit safe).
 */

import { supabase } from "@/integrations/supabase/client";
import { getPeopleAlsoAsk } from "@/services/dataForSeoService";
import { logPublic, logDebug, logError } from "@/services/contentEngine/contentLogger";

const PREFIX = "[PAA-BASELINE]";
const DEFAULT_CONCURRENCY = 50;
const MAX_CONCURRENCY = 100;
const MAX_QUESTIONS_PER_NODE = 5;
const CACHE_TTL_DAYS = 30;

export interface PaaPreviewItem {
  question: string;
  source: "google_paa";
}

export interface PaaBaselineStats {
  total_nodes: number;
  cached: number;
  fetched: number;
  failed: number;
  total_questions: number;
  duration_ms: number;
}

type LogCb = (
  type: "info" | "success" | "error" | "stage",
  message: string,
  debug?: Record<string, unknown>,
) => void;

interface EligibleNode {
  id: string;
  slug: string;
  canonical_question: string | null;
  title: string | null;
  paa_fetched_at: string | null;
}

export async function fetchPaaBaselineForMap(
  mapId: string,
  options: {
    onLog?: LogCb;
    onProgress?: (done: number, total: number) => void;
    concurrency?: number;
    forceRefresh?: boolean;
    locationCode?: number;
    languageCode?: string;
  } = {},
): Promise<PaaBaselineStats> {
  const t0 = Date.now();
  const concurrency = Math.max(
    1,
    Math.min(MAX_CONCURRENCY, options.concurrency ?? DEFAULT_CONCURRENCY),
  );
  const locationCode = options.locationCode ?? 2616; // Poland
  const languageCode = options.languageCode ?? "pl";

  // Eligible: node ma canonical_question (do zapytania PAA) i albo NULL fetched_at albo > 30 dni
  const cutoffDate = new Date(Date.now() - CACHE_TTL_DAYS * 24 * 60 * 60 * 1000).toISOString();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let q = (supabase as any)
    .from("thematic_nodes")
    .select("id, slug, canonical_question, title, paa_fetched_at")
    .eq("map_id", mapId);

  if (!options.forceRefresh) {
    q = q.or(`paa_fetched_at.is.null,paa_fetched_at.lt.${cutoffDate}`);
  }

  const { data, error } = await q;
  if (error) throw new Error(`PAA load eligible failed: ${error.message}`);

  const rows = (data || []) as EligibleNode[];
  const total = rows.length;

  logPublic(PREFIX, `Start PAA baseline fetch: ${total} nodów (concurrency=${concurrency})`);
  options.onLog?.(
    "info",
    `▶ PAA baseline: ${total} nodów do fetchu (DataForSEO, cache ${CACHE_TTL_DAYS}d)`,
  );

  if (total === 0) {
    options.onLog?.("success", "✓ Wszystkie nody mają świeże PAA — nic do pobrania");
    return {
      total_nodes: 0,
      cached: 0,
      fetched: 0,
      failed: 0,
      total_questions: 0,
      duration_ms: Date.now() - t0,
    };
  }

  const stats: PaaBaselineStats = {
    total_nodes: total,
    cached: 0,
    fetched: 0,
    failed: 0,
    total_questions: 0,
    duration_ms: 0,
  };

  const nowIso = () => new Date().toISOString();

  for (let i = 0; i < rows.length; i += concurrency) {
    const chunk = rows.slice(i, i + concurrency);
    const results = await Promise.all(
      chunk.map(async (row) => {
        const query = (row.canonical_question || row.title || "").trim();
        if (!query) {
          return { id: row.id, success: false, error: "empty canonical_question" };
        }
        try {
          const questions = await getPeopleAlsoAsk(query, locationCode, languageCode);
          const top: PaaPreviewItem[] = questions
            .slice(0, MAX_QUESTIONS_PER_NODE)
            .map((qstn) => ({ question: qstn, source: "google_paa" as const }));
          return { id: row.id, slug: row.slug, success: true as const, top };
        } catch (err) {
          return {
            id: row.id,
            slug: row.slug,
            success: false as const,
            error: err instanceof Error ? err.message : String(err),
          };
        }
      }),
    );

    // Batched UPDATE — per node żeby zachować paa_preview/paa_fetched_at atomic
    for (const r of results) {
      if (r.success) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error: updErr } = await (supabase as any)
          .from("thematic_nodes")
          .update({
            paa_preview: r.top,
            paa_fetched_at: nowIso(),
          })
          .eq("id", r.id);
        if (updErr) {
          stats.failed++;
          logError(PREFIX, `Update ${r.id} failed: ${updErr.message}`);
        } else {
          stats.fetched++;
          stats.total_questions += r.top.length;
        }
      } else {
        stats.failed++;
        logError(PREFIX, `PAA fetch failed for ${r.id}: ${"error" in r ? r.error : "unknown"}`);
      }
    }

    const processed = Math.min(i + concurrency, rows.length);
    const pct = total > 0 ? Math.round((processed / total) * 100) : 100;
    if (total > 0 && (processed % 20 === 0 || processed === total)) {
      const lastOk = results.find((r) => r.success);
      options.onLog?.(
        "info",
        `  PAA progress ${pct}% (${processed}/${total}) — fetched=${stats.fetched} failed=${stats.failed}, questions=${stats.total_questions}`,
        lastOk && "top" in lastOk
          ? {
              sample_slug: lastOk.slug,
              sample_questions: lastOk.top.map((q) => q.question),
            }
          : undefined,
      );
      logDebug(PREFIX, `progress ${processed}/${total}`);
    }
    options.onProgress?.(processed, total);
  }

  stats.duration_ms = Date.now() - t0;

  logPublic(
    PREFIX,
    `PAA done ${stats.duration_ms}ms: fetched=${stats.fetched} failed=${stats.failed} questions=${stats.total_questions}`,
  );
  options.onLog?.(
    "stage",
    `✓ PAA baseline done ${(stats.duration_ms / 1000).toFixed(1)}s: fetched=${stats.fetched} · questions=${stats.total_questions} · failed=${stats.failed}`,
    { stats },
  );

  return stats;
}
